/** Recording an in-person meeting from the browser: the microphone, how loud
 * it is (for the ring), and the audio in pieces every few seconds, sent to the
 * server in order as they come — so a dead battery or a closed tab loses
 * seconds, not the meeting. */

import { NotSignedIn, uploadRecordingPiece } from "./api";

/** A piece every ten seconds: small enough that little is lost if the page
 * dies, large enough not to flood a phone's connection. */
const PIECE_MS = 10_000;
const RETRY_MS = [2_000, 5_000, 10_000, 20_000, 30_000];

/** What the browser can record, best first: Chrome and Android make WebM,
 * iPhones and Safari make MP4. The server turns either into MP3. */
export function recordingFormat(): string {
  const formats = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
  return formats.find((type) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) ?? "";
}

/** Why recording can't start here, in words — or null if it can. */
export function cannotRecord(): string | null {
  if (!window.isSecureContext) return "Recording needs a secure (https) address. Open the app's normal address.";
  if (!navigator.mediaDevices?.getUserMedia) return "This browser can't use the microphone.";
  if (typeof MediaRecorder === "undefined") return "This browser can't record audio. Try Chrome, or Safari on an iPhone.";
  return null;
}

/** The microphone, opened. Ask for it on a press of Start: browsers only
 * show the permission prompt in answer to one. */
export async function openMicrophone(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        // A room: nothing plays out of the device, so nothing to cancel; let
        // the browser even out near and far voices and cut the hum.
        echoCancellation: false,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    const name = (error as DOMException)?.name;
    if (name === "NotAllowedError" || name === "SecurityError") {
      throw new Error("Microphone access was blocked. Allow it for this site in the browser's settings, then try again.");
    }
    if (name === "NotFoundError") throw new Error("No microphone was found on this device.");
    throw new Error(`The microphone couldn't be opened (${name ?? "unknown error"}).`);
  }
}

/** How loud the microphone is, 0 to 1, read as often as wanted. */
export function levelMeter(stream: MediaStream): { level: () => number; close: () => void } {
  const AudioContextClass = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new AudioContextClass();
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  context.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  return {
    level: () => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      const rms = Math.sqrt(sum / samples.length);
      // Speech sits around 0.02–0.2 RMS: stretch that to fill 0–1, with a
      // floor so room hum doesn't move the ring.
      return Math.min(1, Math.max(0, (Math.sqrt(rms) - 0.08) * 2.4));
    },
    close: () => {
      context.close().catch(() => undefined);
    },
  };
}

/** Sends the pieces in order, one at a time, retrying a failed one with a
 * growing wait. `pending` is how many are still to go. */
export class PieceUploader {
  private queue: Blob[] = [];
  private seq = 0;
  private sending = false;
  private idle: (() => void)[] = [];
  pending = 0;
  /** The last error, while a piece is being retried; null once it's through. */
  problem: string | null = null;

  constructor(
    private readonly sessionId: string,
    private readonly csrfToken: string,
    private readonly onChange: () => void,
  ) {}

  add(piece: Blob): void {
    if (piece.size === 0) return;
    this.queue.push(piece);
    this.pending = this.queue.length;
    this.onChange();
    void this.send();
  }

  /** Resolves once every piece added so far has been sent. */
  flushed(): Promise<void> {
    if (!this.sending && this.queue.length === 0) return Promise.resolve();
    return new Promise((resolve) => this.idle.push(resolve));
  }

  private async send(): Promise<void> {
    if (this.sending) return;
    this.sending = true;
    let attempt = 0;
    while (this.queue.length) {
      try {
        await uploadRecordingPiece(this.sessionId, this.seq, this.queue[0], this.csrfToken);
        this.queue.shift();
        this.seq += 1;
        attempt = 0;
        this.problem = null;
      } catch (error) {
        if (error instanceof NotSignedIn) this.problem = "You've been signed out. Sign in again in another tab to keep saving.";
        else this.problem = (error as Error).message || "The connection dropped";
        await new Promise((resolve) => setTimeout(resolve, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]));
        attempt += 1;
      }
      this.pending = this.queue.length;
      this.onChange();
    }
    this.sending = false;
    this.idle.splice(0).forEach((resolve) => resolve());
  }
}

/** The recorder itself: pieces every PIECE_MS go to `onPiece`. */
export function startRecorder(stream: MediaStream, mime: string, onPiece: (piece: Blob) => void): MediaRecorder {
  const recorder = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 48_000 });
  recorder.ondataavailable = (event) => onPiece(event.data);
  recorder.start(PIECE_MS);
  return recorder;
}

/** Keeps a phone's screen on while recording: an iPhone stops the microphone
 * when the screen locks. Not every browser can; then the page says so. */
export async function keepScreenOn(): Promise<{ release: () => void } | null> {
  const wakeLock = (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<{ release: () => Promise<void> }> } }).wakeLock;
  if (!wakeLock) return null;
  try {
    const lock = await wakeLock.request("screen");
    return { release: () => void lock.release().catch(() => undefined) };
  } catch {
    return null;
  }
}
